import { useState } from "react";
import { Button } from "@/lib/components/Button/Button";
import { Checkbox } from "@/lib/components/Checkbox";
import PreviewVideoDialog from "./PreviewVideoDialog";
import type { DfyOptionContent } from "../dfy-order-form-content";
import { SELECTED_CARD_STYLE } from "../dfy-order-form-styles";

export default function OptionCard({
  option,
  price,
  isSelected,
  onToggle,
  label = "Add",
  selectedLabel = "Added",
  control = "button",
}: {
  option: DfyOptionContent;
  price?: string;
  isSelected: boolean;
  onToggle: () => void;
  label?: string;
  selectedLabel?: string;
  control?: "button" | "checkbox";
}) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const Icon = option.icon;
  const iconClassName = option.iconClassName ?? "size-5 sm:size-6";

  return (
    <>
      <div
        onClick={onToggle}
        className={`cursor-pointer rounded-xl border ${
          isSelected ? "border-transparent" : "hover:bg-button-outline-hover/40"
        }`}
        style={isSelected ? SELECTED_CARD_STYLE : undefined}
      >
        <div
          className={`flex p-4 sm:items-center sm:gap-4 sm:pr-6 ${
            control === "button"
              ? "flex-col gap-5 sm:flex-row"
              : "items-center gap-3"
          }`}
        >
          <div className="flex min-w-0 flex-1 items-center gap-3 sm:gap-4">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-md sm:size-10">
              {isSelected ? (
                <Icon className={iconClassName} color="url(#icon-gradient)" />
              ) : (
                <Icon className={`${iconClassName} text-text-primary`} />
              )}
            </span>
            <div className="flex min-w-0 flex-1 flex-col">
              <span
                className={`w-fit font-semibold ${
                  isSelected
                    ? "bg-linear-to-r from-primary-400 to-secondary-400 bg-clip-text text-transparent"
                    : ""
                }`}
              >
                {option.name}
              </span>
              <span className="text-sm text-text-secondary">
                {option.description}
              </span>
              {option.preview && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewOpen(true);
                  }}
                  className="mt-1 w-fit cursor-pointer text-sm whitespace-nowrap text-text-secondary underline underline-offset-2"
                >
                  Watch preview
                </button>
              )}
            </div>
            {price && (
              <span className="ml-4 shrink-0 text-sm font-medium text-text-secondary">
                {price}
              </span>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-4">
            {control === "checkbox" ? (
              <Checkbox
                checked={isSelected}
                onCheckedChange={onToggle}
                onClick={(e) => e.stopPropagation()}
                aria-label={option.name}
                className="shrink-0 rounded-full border-border-primary bg-transparent [&_svg]:size-3"
              />
            ) : (
              <Button
                type="button"
                size="sm"
                variant={isSelected ? "gradientBorder" : "outline"}
                aria-pressed={isSelected}
                className="w-full px-3 sm:w-24"
              >
                {isSelected ? (
                  selectedLabel
                ) : (
                  <span className="bg-linear-to-r from-primary-400 to-secondary-400 bg-clip-text text-transparent">
                    {label}
                  </span>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
      {option.preview && (
        <PreviewVideoDialog
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          title={`${option.name} preview`}
          video={option.preview.video}
        />
      )}
    </>
  );
}
